import {
  ErrorBoundary,
  withErrorBoundary,
} from "app/ErrorBoundary/ErrorBoundaryDashboard";
import { CreateEvent } from "features/CenterContent/RoleContent/EventInteraction/CreateEvent";
import { StretchBuilderModalState } from "features/CenterContent/RoleContent/EventInteraction/StretchBuilderModalState";
import {
  TrainmapErrorComponent,
  TrainmapErrorComponentType,
} from "features/CenterContent/RoleContent/TrainMap/TrainMapErrorComponent";
import { TrainMapRenderer } from "features/CenterContent/RoleContent/TrainMap/TrainMapRenderer";
import { useTrainMapTopology } from "features/CenterContent/RoleContent/TrainMap/useTrainMapTopology";
import { useCallback, useMemo, useReducer, useRef } from "react";
import { generatePath, useNavigate } from "react-router-dom";
import { FailureMessage } from "shared/components/feedback/FailureMessage/FailureMessage";
import { Text } from "shared/components/typography/TitleStyles";
import { countryCode } from "shared/types/countryCode";
import * as ROUTES from "shared/utils/routes";
import { useDropsRole } from "stores/useDropsRole";
import { useEventMode, useStretchBuilder } from "stores/useStretchBuilder";
import styled from "styled-components";

const TrainMapWrapper = styled.div`
  position: relative;
  border-radius: 6px;
  height: 100%;
  width: 100%;
  background-color: ${({ theme }) => theme.colorBackgroundMain};
  display: flex;
  align-items: center;
  justify-content: center;
`;

const TrainMap = () => {
  const pageRef = useRef<HTMLDivElement>(null);

  const navigate = useNavigate();

  const [stretchBuilderModalState, setStretchBuilderModalState] =
    useStretchBuilder((state) => [
      state.stretchBuilderModalState,
      state.setStretchBuilderModalState,
    ]);
  const setEventMode = useEventMode((state) => state.setEventMode);

  const { isVaktlederOrDriftledningen, isDriftledningen, isTrafikkplanlegger } =
    useDropsRole();

  const stretchBuilderEnabled = isVaktlederOrDriftledningen();

  const { trainMapTopology, trainMapTopologyStatus } = useTrainMapTopology(
    isDriftledningen() ? countryCode.enum.SE : countryCode.enum.NO,
  );

  const onEventClick = useCallback(
    (uuids: { infrastructure: string[]; station: string[] }) => {
      if (isTrafikkplanlegger() && uuids.infrastructure.length === 1) {
        void navigate(
          generatePath(ROUTES.ALTERNATIVE_TRANSPORT_DECISION_SUPPORT, {
            uuid: uuids.infrastructure[0],
          }),
        );
        return;
      }

      if (stretchBuilderEnabled || isTrafikkplanlegger()) {
        setEventMode(true);
        setStretchBuilderModalState({
          type: "closeOrEdit",
          uuids,
        });
      }
    },

    [
      stretchBuilderEnabled,
      setEventMode,
      setStretchBuilderModalState,
      navigate,
      isTrafikkplanlegger,
    ],
  );

  // Helper functions for the error handler
  const [, forceRefresh] = useReducer((val) => val + 1, 0);
  const errorProps = useMemo<TrainmapErrorComponentType>(
    () => ({ refreshParent: forceRefresh }),
    [],
  );

  const getContent = useCallback(() => {
    switch (trainMapTopologyStatus) {
      case "pending":
        return <Text>Laster togkart ...</Text>;
      case "success":
        return (
          trainMapTopology && (
            <ErrorBoundary
              ErrorComponent={TrainmapErrorComponent}
              errorProps={errorProps}
            >
              <TrainMapRenderer
                topology={trainMapTopology}
                onEventClick={onEventClick}
              />
              <StretchBuilderModalState />
              {stretchBuilderEnabled &&
                isVaktlederOrDriftledningen() &&
                stretchBuilderModalState.type !== "closeOrEdit" && (
                  <CreateEvent />
                )}{" "}
            </ErrorBoundary>
          )
        );
      case "error":
        return <FailureMessage />;
    }
  }, [
    onEventClick,
    stretchBuilderModalState,
    stretchBuilderEnabled,
    trainMapTopology,
    trainMapTopologyStatus,
  ]);

  return <TrainMapWrapper ref={pageRef}>{getContent()}</TrainMapWrapper>;
};

export default withErrorBoundary(TrainMap);
